import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, FileText, DollarSign, Printer, Download, CheckCircle, Edit2, Save, Trash2, BookOpen, X, Loader2, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useFirebaseRoster } from '../hooks/useFirebaseRoster';

// Damage category structure
const DAMAGE_CATEGORIES = {
  'Minor Damage ($2-3)': [
    'Creased pages',
    'Small tears',
    'Stains',
    'Miscellaneous'
  ],
  'Moderate Damage ($4-5)': [
    'Cover separation',
    'Mild spillage',
    'Home repairs',
    'Miscellaneous'
  ],
  'Significant Damage ($6-8)': [
    'Major spine damage',
    'Cover destroyed',
    'Miscellaneous'
  ],
  'Replacement': [
    'Lost',
    'Severe liquid damage',
    'Physical damage',
    'Miscellaneous'
  ]
};

function Mjolnir() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { SCHOOL_ROSTER, loading: rosterLoading, error: rosterError } = useFirebaseRoster();
  const [rawText, setRawText] = useState('');
  const [parsedDamages, setParsedDamages] = useState([]);
  const [unmatchedPatrons, setUnmatchedPatrons] = useState([]);
  const [editingPatron, setEditingPatron] = useState(null);
  const [modalMessage, setModalMessage] = useState(null);

  // Styled modal component with large central eye
  const HeimdalModal = ({ message, onClose }) => {
    if (!message) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div 
          className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{ fontFamily: 'Calibri, sans-serif' }}
        >
          {/* Large central eye */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-8 text-center flex items-center justify-center">
            <Logo className="w-32 h-32" />
          </div>
          
          {/* Message content */}
          <div className="p-6">
            <div className="text-gray-800 whitespace-pre-line text-center" style={{ fontFamily: 'Calibri, sans-serif', fontSize: '16px' }}>
              {message}
            </div>
          </div>
          
          {/* Footer button */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end">
            <button
              onClick={onClose}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition font-semibold"
              style={{ fontFamily: 'Calibri, sans-serif' }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  const parseDamageText = () => {
    if (!rawText.trim()) {
      setModalMessage('Please paste the damage report text first');
      return;
    }

    console.log('Starting parse...'); // Debug
    console.log('Raw text length:', rawText.length); // Debug

    const damages = [];
    
    // Extract patron name and number from the Name line
    // Format: "Name: Sofia Zarzar Apud - 3161"
    const nameMatch = rawText.match(/Name:\s*(.+?)\s*-\s*(\d+)/);
    if (!nameMatch) {
      setModalMessage('Could not find patron name in format "Name: [Name] - [Number]"');
      return;
    }
    
    const patronName = nameMatch[1].trim();
    const patronNumber = nameMatch[2];
    
    console.log('Patron name:', patronName); // Debug
    console.log('Patron number:', patronNumber); // Debug
    
    // Find all DAMAGED items
    // Format: DAMAGED - Overdue! [barcode] [title and call number] [checkout date] [due date]
    const damagedPattern = /DAMAGED\s*-\s*Overdue!\s*(\d+)\s+(.+?)\s+(\d{4}-\d{2}-\d{2})\s+(\d{4}-\d{2}-\d{2})/g;
    const itemMatches = [...rawText.matchAll(damagedPattern)];
    
    console.log('Item matches found:', itemMatches.length); // Debug
    
    if (itemMatches.length === 0) {
      setModalMessage('No DAMAGED items found in the text. Make sure the text contains "DAMAGED - Overdue!" followed by item details.');
      return;
    }

    itemMatches.forEach(match => {
      const barcode = match[1];
      const titleAndCallNum = match[2].trim();
      const checkoutDate = match[3];
      const dueDate = match[4];
      
      console.log('Found damaged item:', barcode, titleAndCallNum); // Debug
      
      // Split title and call number
      // Call numbers are typically at the end and follow patterns like:
      // - "242 HIN" (Dewey decimal + letters)
      // - "FIC PLA" (letters + letters)
      const parts = titleAndCallNum.split(/\s+/);
      let title, callNumber;
      
      if (parts.length <= 2) {
        title = titleAndCallNum;
        callNumber = parts[parts.length - 1] || '';
      } else {
        // Check if last 2 tokens look like a call number
        const lastTwo = parts.slice(-2).join(' ');
        const lastThree = parts.slice(-3).join(' ');
        
        // Common call number patterns
        if (lastThree.match(/^\d{1,3}\s+[A-Z]{2,3}$/) || lastThree.match(/^[A-Z]{2,3}\s+[A-Z]{2,3}\s+[A-Z]{2,3}$/)) {
          callNumber = lastThree;
          title = parts.slice(0, -3).join(' ');
        } else if (lastTwo.match(/^\d{1,3}\s+[A-Z]{2,3}$/) || lastTwo.match(/^[A-Z]{2,3}\s+[A-Z]{2,3}$/)) {
          callNumber = lastTwo;
          title = parts.slice(0, -2).join(' ');
        } else {
          callNumber = parts[parts.length - 1];
          title = parts.slice(0, -1).join(' ');
        }
      }

      // Convert name format from "Sofia Zarzar Apud" to "Zarzar Apud, Sofia" for roster matching
      const nameParts = patronName.split(/\s+/);
      let cleanName = patronName;
      if (nameParts.length >= 2) {
        const firstName = nameParts[0];
        const lastName = nameParts.slice(1).join(' ');
        cleanName = `${lastName}, ${firstName}`;
      }
      
      const patronType = SCHOOL_ROSTER.getPatronType(cleanName);
      let matchedInfo = null;

      if (patronType === 'parent') {
        matchedInfo = SCHOOL_ROSTER.getParent(cleanName);
      } else if (patronType === 'faculty') {
        matchedInfo = SCHOOL_ROSTER.getFaculty(cleanName);
      } else {
        matchedInfo = SCHOOL_ROSTER.getTeacher(cleanName);
      }

      const damage = {
        name: patronName,
        cleanName: cleanName,
        number: patronNumber,
        grade: matchedInfo?.grade || '',
        patronType: patronType,
        matched: !!matchedInfo,
        book: {
          title: title.trim(),
          callNumber: callNumber.trim(),
          barcode: barcode,
          checkoutDate: checkoutDate,
          dueDate: dueDate
        },
        damageCategory: '',
        damageSubcategory: '',
        damageDetails: '',
        cost: '',
        damageId: `${cleanName}-${barcode}`
      };

      if (matchedInfo) {
        if (patronType === 'parent' && matchedInfo.children) {
          damage.children = matchedInfo.children;
          damage.teacher = matchedInfo.teacher;
          damage.grade = matchedInfo.grade;
        } else if (patronType === 'faculty' && matchedInfo.grade) {
          damage.teacher = matchedInfo.name;
          damage.grade = matchedInfo.grade;
        } else if (matchedInfo.teacher) {
          damage.teacher = matchedInfo.teacher;
          damage.grade = matchedInfo.grade || damage.grade;
        }
      }

      damages.push(damage);
    });

    console.log('Total damages found:', damages.length); // Debug
    console.log('Damages:', damages); // Debug

    const matched = damages.filter(d => d.matched);
    const unmatched = damages.filter(d => !d.matched);

    console.log('Matched:', matched.length, 'Unmatched:', unmatched.length); // Debug

    setParsedDamages(matched);
    setUnmatchedPatrons(unmatched);
    
    if (damages.length === 0) {
      setModalMessage('No DAMAGED items found in the text. Make sure the text contains "DAMAGED" followed by a barcode.');
    } else {
      setModalMessage(`Successfully parsed ${damages.length} damaged item(s)!\n\nMatched: ${matched.length}\nNeeds Review: ${unmatched.length}`);
    }
  };

  const updateDamage = (damageId, field, value) => {
    setParsedDamages(prev => prev.map(d => {
      if (d.damageId === damageId) {
        const updated = { ...d, [field]: value };
        // Reset subcategory if category changes
        if (field === 'damageCategory') {
          updated.damageSubcategory = '';
        }
        return updated;
      }
      return d;
    }));
  };

  const savePatronEdit = (damage, newTeacher, newGrade) => {
    const updated = { ...damage, teacher: newTeacher, grade: newGrade, matched: true };
    setUnmatchedPatrons(prev => prev.filter(d => d.damageId !== damage.damageId));
    setParsedDamages(prev => [...prev, updated]);
    setEditingPatron(null);
  };

  const deleteUnmatched = (damage) => {
    setUnmatchedPatrons(prev => prev.filter(d => d.damageId !== damage.damageId));
  };

  const getDamageMessage = (category, subcategory) => {
    // All replacement types get the same "end of library journey" message
    if (category === 'Replacement') {
      return "This book has reached the end of its library journey. The replacement cost allows us to purchase a new copy for all students to enjoy. The damaged book is yours to keep!";
    } else if (category === 'Minor Damage ($2-3)') {
      return "This book needs a little TLC! Minor repairs help keep our books circulating for many more students to enjoy.";
    } else if (category === 'Moderate Damage ($4-5)') {
      if (subcategory === 'Mild spillage') {
        return "This book experienced some water exposure but can be repaired. Quick action can prevent further damage and keep books in circulation.";
      }
      return "This book needs some extra care to stay in circulation. Moderate repairs help extend the life of well-loved books.";
    } else if (category === 'Significant Damage ($6-8)') {
      return "This book has been well-loved! Significant repairs are needed to keep it available for future readers.";
    }
    
    return "Accidents happen! This repair helps ensure all students can continue enjoying our library collection.";
  };

  const generateDamageNoticeHTML = (damage) => {
    const today = new Date().toLocaleDateString();
    const damageMessage = getDamageMessage(damage.damageCategory, damage.damageSubcategory);
    
    // Build damage type display
    let damageTypeDisplay = '';
    if (damage.damageCategory && damage.damageSubcategory) {
      damageTypeDisplay = `${damage.damageCategory}: ${damage.damageSubcategory}`;
      if (damage.damageDetails) {
        damageTypeDisplay += ` - ${damage.damageDetails}`;
      }
    }
    
    const frontPage = `
      <div class="damage-notice-front" style="page-break-after: always; page-break-inside: avoid;">
        <div style="font-family: Calibri, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.3; color: #333; padding: 16px; max-width: 7.5in; margin: 0 auto;">
          <div style="text-align: center; border-bottom: 2px solid #F59E0B; padding-bottom: 4px; margin-bottom: 10px;">
            <div style="font-size: 16px; font-weight: bold; margin-bottom: 1px; color: #EA580C; letter-spacing: 0.5px;">Holy Family Catholic School</div>
            <div style="font-size: 11px; color: #F97316; margin-bottom: 1px; font-weight: 600;">HFCS Library</div>
            <div style="font-size: 9px; color: #666;">9400 Neenah Ave • Austin, TX 78717</div>
          </div>

          <div style="font-size: 17px; font-weight: bold; text-align: center; margin: 10px 0; padding: 10px; background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%); color: #92400E; border: 2px solid #FBBF24; border-radius: 6px;">
            📚 Book Care Notice 📚
          </div>

          <div style="background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%); padding: 10px; border-radius: 6px; margin-bottom: 10px; border: 1px solid #FCD34D;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 11px;">
              <div style="display: flex;">
                <span style="font-weight: bold; width: 65px; color: #92400E;">Student:</span>
                <span style="color: #1F2937;">${damage.cleanName}</span>
              </div>
              <div style="display: flex;">
                <span style="font-weight: bold; width: 65px; color: #92400E;">Teacher:</span>
                <span style="color: #1F2937;">${damage.teacher || 'N/A'}</span>
              </div>
              <div style="display: flex;">
                <span style="font-weight: bold; width: 65px; color: #92400E;">Grade:</span>
                <span style="color: #1F2937;">${damage.grade || 'N/A'}</span>
              </div>
              <div style="display: flex;">
                <span style="font-weight: bold; width: 65px; color: #92400E;">Date:</span>
                <span style="color: #1F2937;">${today}</span>
              </div>
            </div>
          </div>

          <div style="margin-bottom: 10px;">
            <div style="font-size: 13px; font-weight: bold; margin-bottom: 6px; border-bottom: 2px solid #FBBF24; padding-bottom: 3px; color: #EA580C;">
              Book Information
            </div>
            <div style="background: #FFFFFF; border: 1px solid #E5E7EB; padding: 8px; border-radius: 4px;">
              <div style="font-weight: bold; font-size: 11px; margin-bottom: 3px; color: #EA580C;">${damage.book.title}</div>
              <div style="font-size: 9px; color: #6B7280;">
                Call Number: <span style="color: #374151;">${damage.book.callNumber}</span> • 
                Barcode: <span style="color: #374151;">${damage.book.barcode}</span>
              </div>
              <div style="font-size: 9px; color: #6B7280; margin-top: 2px;">
                Checkout: ${damage.book.checkoutDate} • Due: ${damage.book.dueDate}
              </div>
            </div>
          </div>

          <div style="background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%); border: 2px solid #FBBF24; border-radius: 6px; padding: 10px; margin-bottom: 10px;">
            <div style="font-weight: bold; font-size: 12px; margin-bottom: 6px; color: #92400E;">What Happened</div>
            <div style="font-size: 10px; margin-bottom: 6px;">
              <strong>Type of Care Needed:</strong> ${damageTypeDisplay || '_______________________'}
            </div>
            <div style="font-size: 9px; margin-bottom: 6px; color: #78350F; font-style: italic;">
              ${damageMessage}
            </div>

            <div style="border-top: 1px dashed #FBBF24; padding-top: 6px; margin-top: 6px;">
              <div style="font-size: 14px; font-weight: bold; color: #DC2626; text-align: center;">
                Repair/Replacement Cost: ${damage.cost ? `$${parseFloat(damage.cost).toFixed(2)}` : '$_____'}
              </div>
            </div>
          </div>

          <div style="background: #FFFFFF; border: 2px solid #F59E0B; border-radius: 6px; padding: 10px; margin-bottom: 10px;">
            <div style="font-weight: bold; font-size: 11px; margin-bottom: 6px; color: #92400E;">Payment Options - Please Check One:</div>
            <div style="font-size: 10px; display: flex; flex-direction: column; gap: 4px;">
              <label style="display: flex; align-items: center; cursor: pointer;">
                <input type="checkbox" style="margin-right: 6px; width: 14px; height: 14px;" />
                <span>Cash (exact change appreciated)</span>
              </label>
              <label style="display: flex; align-items: center; cursor: pointer;">
                <input type="checkbox" style="margin-right: 6px; width: 14px; height: 14px;" />
                <span>Check (payable to Holy Family Catholic School)</span>
              </label>
              <label style="display: flex; align-items: center; cursor: pointer;">
                <input type="checkbox" style="margin-right: 6px; width: 14px; height: 14px;" />
                <span>Add to FACTS account</span>
              </label>
            </div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #FDE68A; font-size: 9px; color: #78350F;">
              <strong>Parent Signature:</strong> ___________________________________ <strong>Date:</strong> ___________
            </div>
            <div style="font-size: 8px; margin-top: 4px; color: #92400E; font-style: italic;">
              Please return this form with payment or indicate FACTS billing
            </div>
          </div>
        </div>
      </div>
    `;

    const backPage = `
      <div class="damage-notice-back" style="page-break-inside: avoid;">
        <div style="font-family: Calibri, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.4; color: #333; padding: 16px; max-width: 7.5in; margin: 0 auto; font-size: 9px;">
          <div style="text-align: center; border-bottom: 2px solid #F59E0B; padding-bottom: 4px; margin-bottom: 10px;">
            <div style="font-size: 14px; font-weight: bold; color: #EA580C;">Caring for Our Library Books</div>
            <div style="font-size: 10px; color: #92400E;">A Guide for Holy Family Families</div>
          </div>

          <div style="margin-bottom: 10px;">
            <h3 style="font-size: 11px; color: #EA580C; margin-bottom: 3px; font-weight: bold;">Our Library Philosophy 💙</h3>
            <p style="margin: 3px 0;">Books are meant to be read, enjoyed, and loved! They'll naturally show signs of use—that's wonderful and expected! We never want students to feel anxious about checking out books or worried about normal wear and tear. However, when damage occurs that makes a book unsafe or unusable for other students, we need to repair or replace it so everyone can continue to enjoy our collection. This isn't about punishment—it's about teaching stewardship, responsibility, and ensuring every student has access to these wonderful books.</p>
          </div>

          <div style="margin-bottom: 10px;">
            <h3 style="font-size: 11px; color: #EA580C; margin-bottom: 3px; font-weight: bold;">Understanding Repair Costs</h3>
            
            <div style="margin-bottom: 6px;">
              <strong style="color: #92400E;">Repairable Damage:</strong>
              <ul style="margin: 2px 0 0 18px; padding: 0;">
                <li><strong>$2.00 - $3.00 (Minor):</strong> Creased pages, small tears, minor stains, cover creasing</li>
                <li><strong>$4.00 - $5.00 (Moderate):</strong> Cover separation beginning, mild spillage (still usable), attempted home repairs</li>
                <li><strong>$6.00 - $8.00 (Significant):</strong> Cover completely separated, major spine damage, extensive damage to newer books</li>
              </ul>
            </div>
            
            <div style="margin-bottom: 6px;">
              <strong style="color: #92400E;">When Books Need Replacement:</strong><br/>
              Sometimes a book reaches the end of its library journey. When too damaged to repair safely, you'll pay the actual replacement cost + $1 for processing. The damaged book is then yours to keep! We understand these things happen, and we're grateful for your partnership in replacing it.
            </div>
          </div>

          <div style="margin-bottom: 10px;">
            <h3 style="font-size: 11px; color: #EA580C; margin-bottom: 3px; font-weight: bold;">Learning Through Catholic Values ✝️</h3>
            <p style="margin: 3px 0;"><strong>Stewardship:</strong> Caring for things that belong to our community and treating them with respect.</p>
            <p style="margin: 3px 0;"><strong>Responsibility:</strong> Making things right when accidents happen—it's part of growing in virtue.</p>
            <p style="margin: 3px 0;"><strong>Justice:</strong> Understanding that when we damage something shared, justice calls us to help restore it for others.</p>
            <p style="margin: 3px 0;"><strong>Honesty:</strong> We always appreciate when students tell us what happened! Honesty helps us assess fairly and shows integrity.</p>
          </div>

          <div style="margin-bottom: 10px;">
            <h3 style="font-size: 11px; color: #EA580C; margin-bottom: 3px; font-weight: bold;">The Four Biggest Book Dangers 🚨</h3>
            <ol style="margin: 3px 0 0 18px; padding: 0;">
              <li><strong>Backpack Crush:</strong> Books squashed under heavy items. Keep them in a protected pocket or on top!</li>
              <li><strong>Liquid Spills:</strong> Water bottles leak, juice boxes spill. Keep books far away from all drinks!</li>
              <li><strong>Food & Snacks:</strong> Eating while reading can damage pages and attract pests. Save snacks for after reading!</li>
              <li><strong>Poor Storage:</strong> Books on floors, in cars, or near pets get damaged easily. Give them a safe, dry home!</li>
            </ol>
          </div>

          <div style="margin-bottom: 10px;">
            <h3 style="font-size: 11px; color: #EA580C; margin-bottom: 3px; font-weight: bold;">Please Don't Try Home Repairs! 🔧</h3>
            <p style="margin: 3px 0;">We know you want to help, but regular tape and glue can actually damage books more over time! We use special archival materials designed for books. If something happens, just return the book to us with a note explaining what occurred—we'll take it from there!</p>
          </div>

          <div style="margin-bottom: 10px;">
            <h3 style="font-size: 11px; color: #EA580C; margin-bottom: 3px; font-weight: bold;">Frequently Asked Questions</h3>
            
            <p style="margin: 3px 0;"><strong>Q: Can I just buy a replacement book instead?</strong><br/>
            A: Yes, with approval! The book must be identical (same edition/ISBN). Please check with us BEFORE purchasing to make sure it will work. For most books, paying the repair/replacement fee is simpler and ensures we get exactly what we need.</p>
            
            <p style="margin: 3px 0;"><strong>Q: My child said the book was already damaged when checked out?</strong><br/>
            A: Students should always check books carefully at checkout and let us know immediately if they notice damage. If you find pre-existing damage at home, please send the book back with a note right away so we can investigate.</p>
            
            <p style="margin: 3px 0;"><strong>Q: What if it was damaged by a younger sibling, pet, or houseguest?</strong><br/>
            A: We completely understand—life happens! The student who checked out the book is ultimately responsible, but we always approach these situations with grace and understanding. We're all learning together.</p>
            
            <p style="margin: 3px 0;"><strong>Q: Will this affect my child's grades or library privileges?</strong><br/>
            A: No impact on grades! However, if there's an outstanding balance, checkout privileges may be temporarily limited until resolved. Once paid, full privileges are restored immediately. All obligations must be cleared by year-end and before graduation.</p>
            
            <p style="margin: 3px 0;"><strong>Q: What if I disagree with the damage assessment?</strong><br/>
            A: Please come talk to Dr. Kahn! We're always happy to discuss our assessment, show you the book, and hear your perspective. Communication and understanding are important to us.</p>
          </div>

          <div style="background: #FEF3C7; border: 2px solid #F59E0B; border-radius: 6px; padding: 8px; margin-bottom: 10px;">
            <h3 style="font-size: 11px; color: #92400E; margin-bottom: 3px; font-weight: bold;">💛 Financial Hardship? We're Here to Help</h3>
            <p style="margin: 3px 0; font-size: 9px;">Every child deserves access to books and our library, regardless of financial circumstances. If you're experiencing financial hardship, please contact Dr. Kahn confidentially at vkahn@holyfamilycs.org. We have options available and want to work with you. Please don't let cost be a barrier—reach out and let's find a solution together.</p>
          </div>

          <div style="margin-top: 12px; padding-top: 8px; border-top: 2px solid #F59E0B; text-align: center; font-size: 8px;">
            <strong style="color: #EA580C;">Dr. Kahn, Librarian</strong><br/>
            📧 vkahn@holyfamilycs.org<br/>
            <em style="color: #6B7280;">Thank you for partnering with us to care for our library collection! 📚</em>
          </div>
        </div>
      </div>
    `;

    return frontPage + backPage;
  };

  const downloadNotice = (damage) => {
    const html = `<!DOCTYPE html>
      <html>
        <head>
          <title>Book Care Notice - ${damage.cleanName}</title>
          <style>
            @page { margin: 0.5in; size: letter; }
            @media print {
              body { margin: 0; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>${generateDamageNoticeHTML(damage)}</body>
      </html>`;
    
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = damage.cleanName.replace(/[^a-zA-Z0-9]/g, '_');
    a.href = url;
    a.download = `BookCareNotice_${safeName}_${damage.book.barcode}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printNotice = (damage) => {
    const html = `<!DOCTYPE html>
      <html>
        <head>
          <title>Book Care Notice - ${damage.cleanName}</title>
          <style>
            @page { margin: 0.5in; size: letter; }
            @media print {
              body { margin: 0; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>${generateDamageNoticeHTML(damage)}</body>
      </html>`;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    } else {
      setModalMessage('Unable to open print window. Please allow popups for this site, or use the download option instead.');
    }
  };

  const printAll = () => {
    const validDamages = parsedDamages.filter(d => d.damageCategory && d.damageSubcategory && d.cost);
    if (validDamages.length === 0) {
      setModalMessage('Please set damage category, subcategory, and cost for at least one item before printing.');
      return;
    }

    const allHTML = validDamages.map(damage => generateDamageNoticeHTML(damage)).join('');
    const html = `<!DOCTYPE html>
      <html>
        <head>
          <title>All Book Care Notices</title>
          <style>
            @page { margin: 0.5in; size: letter; }
            @media print {
              body { margin: 0; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>${allHTML}</body>
      </html>`;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    } else {
      setModalMessage('Unable to open print window. Please allow popups for this site, or use the download option instead.');
    }
  };

  const downloadAll = () => {
    const validDamages = parsedDamages.filter(d => d.damageCategory && d.damageSubcategory && d.cost);
    if (validDamages.length === 0) {
      setModalMessage('Please set damage category, subcategory, and cost for at least one item before downloading.');
      return;
    }

    const allHTML = validDamages.map(damage => generateDamageNoticeHTML(damage)).join('');
    const html = `<!DOCTYPE html>
      <html>
        <head>
          <title>All Book Care Notices</title>
          <style>
            @page { margin: 0.5in; size: letter; }
            @media print {
              body { margin: 0; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>${allHTML}</body>
      </html>`;
    
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `All_BookCareNotices_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Modal */}
      <HeimdalModal message={modalMessage} onClose={() => setModalMessage(null)} />

      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo className="w-12 h-12" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>Mjölnir</h1>
                <p className="text-purple-200 text-sm" style={{ fontFamily: 'Calibri, sans-serif' }}>Book Damage Assessment</p>
              </div>
            </div>
            <nav className="flex items-center space-x-3 sm:space-x-4">
              <button
                onClick={() => navigate('/asgard')}
                className="text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-700 rounded-lg hover:bg-purple-600 transition"
                style={{ fontFamily: 'Calibri, sans-serif' }}
              >
                ← Back to Asgard
              </button>
              <button
                onClick={logout}
                className="text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 rounded-lg hover:bg-red-700 transition"
                style={{ fontFamily: 'Calibri, sans-serif' }}
              >
                Logout
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Input */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center" style={{ fontFamily: 'Calibri, sans-serif' }}>
                <FileText className="mr-2 text-purple-600" size={24} />
                Parse Damage Reports
              </h2>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="Paste LibraryWorld damage report here..."
                className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                style={{ fontFamily: 'Consolas, Monaco, monospace' }}
              />
              <button
                onClick={parseDamageText}
                className="mt-4 w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white py-3 px-6 rounded-lg hover:from-yellow-600 hover:to-yellow-700 transition font-bold shadow-md"
                style={{ fontFamily: 'Calibri, sans-serif' }}
              >
                ⚡ Parse Damage Reports
              </button>
            </div>

            {/* Parsed Damages */}
            {parsedDamages.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center" style={{ fontFamily: 'Calibri, sans-serif' }}>
                  <CheckCircle className="mr-2 text-green-600" size={24} />
                  Matched Damages ({parsedDamages.length})
                </h2>
                
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {parsedDamages.map((damage) => (
                    <div key={damage.damageId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="mb-3">
                        <div className="font-semibold text-gray-800" style={{ fontFamily: 'Calibri, sans-serif' }}>{damage.cleanName}</div>
                        <div className="text-sm text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>
                          Teacher: {damage.teacher || 'N/A'} • Grade: {damage.grade || 'N/A'}
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded p-3 mb-3">
                        <div className="flex items-start">
                          <BookOpen className="mr-2 text-purple-600 flex-shrink-0 mt-1" size={16} />
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-800" style={{ fontFamily: 'Calibri, sans-serif' }}>{damage.book.title}</div>
                            <div className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Calibri, sans-serif' }}>
                              Call#: {damage.book.callNumber} • Barcode: {damage.book.barcode}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Main Category Dropdown */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1" style={{ fontFamily: 'Calibri, sans-serif' }}>
                            Damage Category:
                          </label>
                          <select
                            value={damage.damageCategory}
                            onChange={(e) => updateDamage(damage.damageId, 'damageCategory', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500"
                            style={{ fontFamily: 'Calibri, sans-serif' }}
                          >
                            <option value="">Select category...</option>
                            {Object.keys(DAMAGE_CATEGORIES).map(category => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        </div>

                        {/* Subcategory Dropdown - only shows if category selected */}
                        {damage.damageCategory && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1" style={{ fontFamily: 'Calibri, sans-serif' }}>
                              Specific Type:
                            </label>
                            <select
                              value={damage.damageSubcategory}
                              onChange={(e) => updateDamage(damage.damageId, 'damageSubcategory', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500"
                              style={{ fontFamily: 'Calibri, sans-serif' }}
                            >
                              <option value="">Select specific type...</option>
                              {DAMAGE_CATEGORIES[damage.damageCategory].map(subcategory => (
                                <option key={subcategory} value={subcategory}>{subcategory}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Optional Details Text Field - ALWAYS shows if subcategory selected */}
                        {damage.damageSubcategory && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1" style={{ fontFamily: 'Calibri, sans-serif' }}>
                              Additional Details (optional):
                            </label>
                            <textarea
                              value={damage.damageDetails || ''}
                              onChange={(e) => updateDamage(damage.damageId, 'damageDetails', e.target.value)}
                              placeholder="Optional specific details (e.g., 'front cover corner', 'pages 15-20', 'spine at binding')..."
                              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 resize-none"
                              rows="2"
                              style={{ fontFamily: 'Calibri, sans-serif' }}
                            />
                            <div className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Calibri, sans-serif' }}>
                              Only appears on notice if you add text. Leave blank if not needed.
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1" style={{ fontFamily: 'Calibri, sans-serif' }}>
                            <DollarSign className="inline" size={14} /> Cost:
                          </label>
                          <div className="flex items-center">
                            <span className="text-gray-600 mr-2">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={damage.cost}
                              onChange={(e) => updateDamage(damage.damageId, 'cost', e.target.value)}
                              placeholder="0.00"
                              className="flex-1 p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500"
                              style={{ fontFamily: 'Calibri, sans-serif' }}
                            />
                          </div>
                        </div>
                      </div>

                      {damage.damageCategory && damage.damageSubcategory && damage.cost && (
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => downloadNotice(damage)}
                            className="flex-1 bg-green-600 text-white py-2 px-3 rounded hover:bg-green-700 flex items-center justify-center text-sm"
                            style={{ fontFamily: 'Calibri, sans-serif' }}
                          >
                            <Download size={16} className="mr-1" /> Download
                          </button>
                          <button
                            onClick={() => printNotice(damage)}
                            className="flex-1 bg-purple-600 text-white py-2 px-3 rounded hover:bg-purple-700 flex items-center justify-center text-sm"
                            style={{ fontFamily: 'Calibri, sans-serif' }}
                          >
                            <Printer size={16} className="mr-1" /> Print
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unmatched Patrons */}
            {unmatchedPatrons.length > 0 && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-yellow-900 mb-4 flex items-center" style={{ fontFamily: 'Calibri, sans-serif' }}>
                  <AlertTriangle className="mr-2 text-yellow-600" size={24} />
                  Patrons Not Found ({unmatchedPatrons.length})
                </h2>
                <p className="text-sm text-yellow-800 mb-4" style={{ fontFamily: 'Calibri, sans-serif' }}>
                  These patrons were not found in the roster. Manually assign teacher/grade or delete.
                </p>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {unmatchedPatrons.map((damage) => (
                    <div key={damage.damageId} className="bg-white border border-yellow-200 rounded-lg p-4">
                      {editingPatron?.damageId === damage.damageId ? (
                        <div className="space-y-3">
                          <div className="font-semibold text-gray-800" style={{ fontFamily: 'Calibri, sans-serif' }}>{damage.cleanName}</div>
                          <div className="text-sm text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>{damage.book.title}</div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="Teacher name"
                              defaultValue={editingPatron.teacher}
                              onChange={(e) => setEditingPatron({...editingPatron, teacher: e.target.value})}
                              className="p-2 border border-gray-300 rounded text-sm"
                              style={{ fontFamily: 'Calibri, sans-serif' }}
                            />
                            <input
                              type="text"
                              placeholder="Grade"
                              defaultValue={editingPatron.grade}
                              onChange={(e) => setEditingPatron({...editingPatron, grade: e.target.value})}
                              className="p-2 border border-gray-300 rounded text-sm"
                              style={{ fontFamily: 'Calibri, sans-serif' }}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => savePatronEdit(damage, editingPatron.teacher, editingPatron.grade)}
                              className="flex-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 flex items-center justify-center"
                              style={{ fontFamily: 'Calibri, sans-serif' }}
                            >
                              <Save size={16} className="mr-1" /> Save
                            </button>
                            <button
                              onClick={() => setEditingPatron(null)}
                              className="flex-1 bg-gray-400 text-white px-3 py-2 rounded hover:bg-gray-500"
                              style={{ fontFamily: 'Calibri, sans-serif' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-800" style={{ fontFamily: 'Calibri, sans-serif' }}>{damage.cleanName}</div>
                            <div className="text-sm text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>{damage.book.title}</div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingPatron(damage)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Edit"
                            >
                              <Edit2 size={20} />
                            </button>
                            <button
                              onClick={() => deleteUnmatched(damage)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Stats & Actions */}
          <div className="space-y-6">
            {parsedDamages.length > 0 && (
              <>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Calibri, sans-serif' }}>Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>Total Damages:</span>
                      <span className="font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>{parsedDamages.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>Ready to Print:</span>
                      <span className="font-bold text-green-600" style={{ fontFamily: 'Calibri, sans-serif' }}>
                        {parsedDamages.filter(d => d.damageCategory && d.damageSubcategory && d.cost).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>Need Review:</span>
                      <span className="font-bold text-yellow-600" style={{ fontFamily: 'Calibri, sans-serif' }}>
                        {unmatchedPatrons.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>Total Books:</span>
                      <span className="font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>
                        {parsedDamages.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600" style={{ fontFamily: 'Calibri, sans-serif' }}>Total Value:</span>
                      <span className="font-bold text-red-600" style={{ fontFamily: 'Calibri, sans-serif' }}>
                        ${parsedDamages.reduce((sum, d) => sum + (parseFloat(d.cost) || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Calibri, sans-serif' }}>Download & Print</h3>
                  <div className="space-y-3">
                    <button
                      onClick={downloadAll}
                      className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 transition font-medium flex items-center justify-center"
                      style={{ fontFamily: 'Calibri, sans-serif' }}
                    >
                      <Download className="mr-2" size={20} />
                      Download All
                    </button>
                    <button
                      onClick={printAll}
                      className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition font-medium flex items-center justify-center"
                      style={{ fontFamily: 'Calibri, sans-serif' }}
                    >
                      <Printer className="mr-2" size={20} />
                      Print All
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-3" style={{ fontFamily: 'Calibri, sans-serif' }}>
                    Only items with category, subcategory, and cost will be included
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Mjolnir;